/**
 * Advance-without-PI approver queue — rendered as the "Advance w/o PI" tab on
 * the Vendor Invoice Approvals page. Distinct from the normal advance flow: own
 * endpoints (deviated-advance-requests/*) and its own decision call. Only the
 * first-stage approver can act; a 403 renders an approver-only notice.
 */

import React, { useRef, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, ShieldAlert, AlertTriangle, Paperclip, Upload } from 'lucide-react';
import { Button } from '../../components/ui';
import { cn } from '../../utils/cn';
import { ProcurementApiService, DeviatedAdvanceRequest } from '../../services/procurementApi';

function formatAmount(v: number | null | undefined): string {
	if (v == null) return '—';
	return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

function formatDate(v: string | null | undefined): string {
	if (!v) return '—';
	try {
		const d = new Date(v);
		return Number.isNaN(d.getTime())
			? v
			: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
	} catch {
		return v;
	}
}

interface Props {
	requests: DeviatedAdvanceRequest[];
	loading: boolean;
	/** True when the API returned 403 — the caller is not a first-stage approver. */
	forbidden: boolean;
	/** Called after a successful approve/reject so the parent can refetch. */
	onActionDone: () => void;
}

type Mode = 'approve' | 'reject' | null;

const DeviatedAdvanceApprovalList: React.FC<Props> = ({ requests, loading, forbidden, onActionDone }) => {
	const [actionFor, setActionFor] = useState<number | null>(null);
	const [mode, setMode] = useState<Mode>(null);
	const [amount, setAmount] = useState('');
	const [reason, setReason] = useState('');
	const [reasonError, setReasonError] = useState('');
	const [saving, setSaving] = useState(false);

	const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
	const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const showToast = (type: 'success' | 'error', msg: string) => {
		setToast({ type, msg });
		if (toastRef.current) clearTimeout(toastRef.current);
		toastRef.current = setTimeout(() => setToast(null), 4000);
	};

	const [slipBusy, setSlipBusy] = useState<number | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const slipTargetRef = useRef<number | null>(null);
	const pickSlip = (id: number) => {
		slipTargetRef.current = id;
		fileRef.current?.click();
	};
	const handleSlipFile = async (file: File | null) => {
		const id = slipTargetRef.current;
		if (!file || id == null) return;
		setSlipBusy(id);
		try {
			await ProcurementApiService.attachDeviatedAdvanceSlip(id, file);
			showToast('success', 'Payment slip attached.');
			onActionDone();
		} catch (e: any) {
			showToast('error', e?.response?.data?.message || e?.message || 'Failed to attach slip.');
		} finally {
			setSlipBusy(null);
			if (fileRef.current) fileRef.current.value = '';
		}
	};

	const stageLabel = (s: 'first' | 'finance' | null) =>
		s === 'first' ? 'Stage 1 — first approval' : s === 'finance' ? 'Stage 2 — finance' : null;

	const openApprove = (req: DeviatedAdvanceRequest) => {
		setActionFor(req.id);
		setMode('approve');
		setAmount(String(req.amountRequested));
		setReason('');
		setReasonError('');
	};
	const openReject = (req: DeviatedAdvanceRequest) => {
		setActionFor(req.id);
		setMode('reject');
		setReason('');
		setReasonError('');
	};
	const close = () => {
		setActionFor(null);
		setMode(null);
	};

	const submitApprove = async (req: DeviatedAdvanceRequest) => {
		const parsed = parseFloat(amount.replace(/,/g, ''));
		if (Number.isNaN(parsed) || parsed <= 0) {
			showToast('error', 'Enter a valid approved amount.');
			return;
		}
		setSaving(true);
		try {
			await ProcurementApiService.submitDeviatedAdvanceDecision(req.id, {
				decision: 'approved',
				amountApproved: parsed,
			});
			showToast('success', 'Advance request approved.');
			close();
			onActionDone();
		} catch (e: any) {
			showToast('error', e?.response?.data?.message || e?.message || 'Failed to approve.');
		} finally {
			setSaving(false);
		}
	};

	const submitReject = async (req: DeviatedAdvanceRequest) => {
		if (!reason.trim()) {
			setReasonError('Rejection reason is required');
			return;
		}
		setSaving(true);
		try {
			await ProcurementApiService.submitDeviatedAdvanceDecision(req.id, {
				decision: 'rejected',
				rejectionReason: reason.trim(),
			});
			showToast('success', 'Advance request rejected. The team has been notified.');
			close();
			onActionDone();
		} catch (e: any) {
			showToast('error', e?.response?.data?.message || e?.message || 'Failed to reject.');
		} finally {
			setSaving(false);
		}
	};

	if (forbidden) {
		return (
			<div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
				<ShieldAlert className="h-12 w-12 text-gray-300" />
				<p className="text-sm font-medium text-gray-500">Approver access only</p>
				<p className="text-xs text-gray-400 max-w-sm">
					Only the first-stage approver can review advance-without-PI requests.
				</p>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400">
				<Loader2 className="h-4 w-4 animate-spin" /> Loading…
			</div>
		);
	}

	if (requests.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
				<CheckCircle2 className="h-12 w-12 text-green-200" />
				<p className="text-sm font-medium text-gray-500">All caught up!</p>
				<p className="text-xs text-gray-400">No advance-without-PI requests pending your approval.</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{toast && (
				<div
					className={cn(
						'fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium',
						toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
					)}
				>
					{toast.msg}
				</div>
			)}

			<input
				ref={fileRef}
				type="file"
				accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
				className="hidden"
				onChange={e => handleSlipFile(e.target.files?.[0] ?? null)}
			/>

			{requests.map(req => {
				const acting = actionFor === req.id;
				return (
					<div key={req.id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
						<div className="flex items-start justify-between gap-4">
							<div className="min-w-0 space-y-1">
								<p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
									{req.leadClient ?? '—'} · Lead #{req.leadId}
								</p>
								<div className="flex items-center gap-2 flex-wrap">
									<p className="text-sm font-semibold text-gray-900">{req.vendorName}</p>
									<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
										<AlertTriangle className="h-2.5 w-2.5" />
										{req.workflowDeviationLabel}
									</span>
									{stageLabel(req.pendingStage) && (
										<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
											{stageLabel(req.pendingStage)}
										</span>
									)}
								</div>
								<p className="text-xs text-gray-400">Requested: {formatDate(req.createdAt)}</p>
							</div>
							<div className="text-right shrink-0">
								<p className="text-base font-extrabold tabular-nums text-gray-900">
									{formatAmount(req.amountRequested)}
								</p>
								<p className="text-[10px] text-gray-400 mt-0.5">
									requested{req.fullAmount != null ? ` · of ${formatAmount(req.fullAmount)}` : ''}
								</p>
							</div>
						</div>

						<div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
							<span className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
								Justification
							</span>
							{req.justification || '—'}
						</div>

						<div className="mt-3 flex items-center gap-3 flex-wrap">
							<span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
								Payment slips
							</span>
							{req.paymentSlipUrls.length > 0 ? (
								req.paymentSlipUrls.map((url, i) => (
									<a
										key={url}
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
									>
										<Paperclip className="h-3 w-3" />
										Slip {req.paymentSlipUrls.length > 1 ? i + 1 : ''}
									</a>
								))
							) : (
								<span className="text-xs text-gray-400">none</span>
							)}
							<button
								onClick={() => pickSlip(req.id)}
								disabled={slipBusy === req.id}
								className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
							>
								{slipBusy === req.id ? (
									<Loader2 className="h-3 w-3 animate-spin" />
								) : (
									<Upload className="h-3 w-3" />
								)}
								Attach slip
							</button>
						</div>

						<div className="mt-3 flex items-center justify-between gap-3">
							{req.proofFileUrls.length > 0 ? (
								<div className="flex flex-wrap items-center gap-3">
									{req.proofFileUrls.map((url, i) => (
										<a
											key={url}
											href={url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
										>
											<Paperclip className="h-3 w-3" />
											Proof {req.proofFileUrls.length > 1 ? i + 1 : ''}
										</a>
									))}
								</div>
							) : (
								<span className="text-xs text-gray-400">No proof</span>
							)}

							{!acting && (
								<div className="flex items-center gap-2">
									<button
										onClick={() => openReject(req)}
										className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
									>
										<XCircle className="h-3.5 w-3.5" /> Reject
									</button>
									<button
										onClick={() => openApprove(req)}
										className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
									>
										<CheckCircle2 className="h-3.5 w-3.5" /> Approve
									</button>
								</div>
							)}
						</div>

						{acting && mode === 'approve' && (
							<div className="mt-3 p-3 bg-green-50/60 border border-green-100 rounded-lg space-y-2">
								<label className="block text-xs font-medium text-gray-700">Approved amount (₹)</label>
								<input
									type="number"
									inputMode="decimal"
									min="1"
									step="0.01"
									value={amount}
									onChange={e => setAmount(e.target.value)}
									className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-green-400"
								/>
								<div className="flex justify-end gap-2">
									<Button variant="outline" size="md" onClick={close} disabled={saving}>
										Cancel
									</Button>
									<Button
										variant="primary"
										size="md"
										onClick={() => submitApprove(req)}
										disabled={saving}
										className="bg-green-600 hover:bg-green-700 border-green-600"
									>
										{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
										Confirm Approve
									</Button>
								</div>
							</div>
						)}

						{acting && mode === 'reject' && (
							<div className="mt-3 p-3 bg-red-50/60 border border-red-100 rounded-lg space-y-2">
								<label className="block text-xs font-medium text-gray-700">
									Reason for rejection <span className="text-red-500">*</span>
								</label>
								<textarea
									rows={2}
									value={reason}
									onChange={e => {
										setReason(e.target.value);
										setReasonError('');
									}}
									placeholder="e.g. Insufficient documentation"
									className={cn(
										'w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400',
										reasonError ? 'border-red-400' : 'border-gray-300'
									)}
								/>
								{reasonError && <p className="text-xs text-red-500">{reasonError}</p>}
								<div className="flex justify-end gap-2">
									<Button variant="outline" size="md" onClick={close} disabled={saving}>
										Cancel
									</Button>
									<Button
										variant="primary"
										size="md"
										onClick={() => submitReject(req)}
										disabled={saving}
										className="bg-red-600 hover:bg-red-700 border-red-600"
									>
										{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
										Confirm Rejection
									</Button>
								</div>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default DeviatedAdvanceApprovalList;
